export type ShellMode = 'browser' | 'embedded';

export type EmbeddedScreenState =
  | 'booting'
  | 'opening_launcher'
  | 'waiting_launcher_login'
  | 'reconnecting'
  | 'authenticated';

export const LOGIN_ROUTE = '/login';
export const EMBEDDED_ENTRY_ROUTE = '/embedded';
export const DEFAULT_EMBEDDED_ROUTE = '/main';

const SHELL_MODE_STORAGE_KEY = 'one:shell-mode';
const EMBEDDED_LAST_ROUTE_KEY = 'one:embedded-last-route';
const EMBEDDED_SCREEN_STATE_KEY = 'one:embedded-screen-state';

export function isEmbeddedRoute(pathname: string | null | undefined): boolean {
  return pathname === EMBEDDED_ENTRY_ROUTE;
}

export function isPublicAppRoute(pathname: string | null | undefined): boolean {
  return pathname === '/' || pathname === LOGIN_ROUTE || pathname === EMBEDDED_ENTRY_ROUTE;
}

export function isProtectedAppRoute(pathname: string | null | undefined): boolean {
  return !!pathname && !isPublicAppRoute(pathname);
}

export function getStoredShellMode(): ShellMode {
  if (typeof window === 'undefined') {
    return 'browser';
  }

  return window.sessionStorage.getItem(SHELL_MODE_STORAGE_KEY) === 'embedded'
    ? 'embedded'
    : 'browser';
}

export function storeShellMode(mode: ShellMode): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(SHELL_MODE_STORAGE_KEY, mode);
}

export function getStoredEmbeddedScreenState(): EmbeddedScreenState {
  if (typeof window === 'undefined') {
    return 'authenticated';
  }

  const value = window.sessionStorage.getItem(EMBEDDED_SCREEN_STATE_KEY);
  switch (value) {
    case 'booting':
    case 'opening_launcher':
    case 'waiting_launcher_login':
    case 'reconnecting':
    case 'authenticated':
      return value;
    default:
      return 'authenticated';
  }
}

export function storeEmbeddedScreenState(state: EmbeddedScreenState): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(EMBEDDED_SCREEN_STATE_KEY, state);
}

export function rememberEmbeddedRoute(pathname: string | null | undefined): void {
  if (typeof window === 'undefined' || !isProtectedAppRoute(pathname)) {
    return;
  }

  window.sessionStorage.setItem(EMBEDDED_LAST_ROUTE_KEY, pathname);
}

export function getEmbeddedReturnRoute(fallback: string = DEFAULT_EMBEDDED_ROUTE): string {
  if (typeof window === 'undefined') {
    return fallback;
  }

  const storedRoute = window.sessionStorage.getItem(EMBEDDED_LAST_ROUTE_KEY);
  return isProtectedAppRoute(storedRoute) ? storedRoute : fallback;
}
